/* HER PICTURES, KEPT IN BRILL (2026-10-27).
 *
 * Most clients have no GHL (her direction, 2026-10-26), so Brill holds the
 * pictures itself and GHL, Wix and the rest are places to send them. This is
 * Supabase Storage: bucket `pictures`, one folder per client named by her
 * sign-in id. supabase/0047 is the other half of every rule here — the
 * bucket's types and size, and the policy that a client lists and adds ONLY
 * inside her own folder. `brillPictures.test.js` holds the two halves to the
 * same numbers.
 *
 * Every picture is a public link (a builder has to be able to fetch it) with a
 * random part in its name, so a link can be shared but not guessed. Every
 * field that takes a picture still stores just that URL — nothing downstream
 * knows or cares that it came from here.
 */

export const PICTURE_BUCKET = "pictures";
/* No SVG: a public SVG can carry script. PNG keeps a logo's transparency. */
export const PICTURE_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];
export const PICTURE_LIMIT = 5 * 1024 * 1024;

const randomPart = () => {
  try { return crypto.randomUUID().replace(/-/g, "").slice(0, 12); }
  catch { return Math.random().toString(36).slice(2, 14); }
};

/** A file name that is safe in a URL and still recognisable to her. */
export function safeName(name) {
  const s = String(name || "picture").toLowerCase()
    .replace(/[^a-z0-9.]+/g, "-").replace(/-+/g, "-").replace(/^[-.]+|-+$/g, "");
  return (s || "picture").slice(-60);
}

/** Where a picture goes. The FIRST segment must be her id — the policy checks exactly that. */
export function picturePath(userId, fileName, stamp = Date.now(), rand = randomPart()) {
  if (!userId) throw new Error("Not signed in.");
  return `${userId}/${stamp.toString(36)}-${rand}-${safeName(fileName)}`;
}

/** The name she sees: the stored name without its stamp and random part. */
export function pictureName(objectName) {
  const base = String(objectName || "").split("/").pop();
  return base.replace(/^[a-z0-9]+-[a-z0-9]{6,}-/, "") || base;
}

const toPicture = (sb, userId, obj) => {
  const path = `${userId}/${obj.name}`;
  const { data } = sb.storage.from(PICTURE_BUCKET).getPublicUrl(path);
  return { id: path, name: pictureName(obj.name), url: data.publicUrl };
};

/** Her pictures, newest first. @returns {{ ok, pictures } | { ok: false, error }} */
export async function listPictures(sb, userId) {
  if (!userId) return { ok: false, error: "Sign in to see your pictures." };
  const { data, error } = await sb.storage.from(PICTURE_BUCKET)
    .list(userId, { limit: 200, sortBy: { column: "created_at", order: "desc" } });
  if (error) return { ok: false, error: `Couldn't open your pictures — ${error.message}` };
  /* A folder placeholder Supabase keeps is not a picture. */
  const files = (data || []).filter((o) => o.id && o.name !== ".emptyFolderPlaceholder");
  return { ok: true, pictures: files.map((o) => toPicture(sb, userId, o)) };
}

/** Is this a picture Brill will keep? Asked before the upload, so the refusal is in her words. */
export function checkPicture(contentType, size) {
  if (!PICTURE_TYPES.includes(String(contentType || "").toLowerCase())) {
    return { ok: false, error: "Brill keeps PNG, JPG, GIF and WebP pictures. Save an SVG as a PNG first." };
  }
  if (size > PICTURE_LIMIT) return { ok: false, error: "That picture is over 5 MB. Try a smaller one." };
  return { ok: true };
}

/** Upload one picture into her folder. @returns {{ ok, picture } | { ok: false, error }} */
export async function savePicture(sb, userId, blob, fileName) {
  const contentType = blob?.type || "";
  const fit = checkPicture(contentType, blob?.size || 0);
  if (!fit.ok) return fit;
  let path;
  try { path = picturePath(userId, fileName); } catch (err) { return { ok: false, error: err.message }; }
  const { error } = await sb.storage.from(PICTURE_BUCKET)
    .upload(path, blob, { contentType, cacheControl: "31536000", upsert: false });
  if (error) return { ok: false, error: `The upload didn't go through — ${error.message}. Nothing was added.` };
  return { ok: true, picture: toPicture(sb, userId, { name: path.slice(userId.length + 1) }) };
}
